import { useCallback, useMemo } from "react";

import { Checkbox } from "@/components/ui/checkbox";

interface FacetOption {
  value: string;
  display_name: string;
  count: number;
}

interface Facet {
  name: string;
  options: FacetOption[];
}

interface SelectedFacetItem {
  value: string;
  display_name: string;
}

interface SelectedFacets {
  [key: string]: SelectedFacetItem[];
}

interface ShowMoreState {
  [key: string]: boolean;
}

interface FacetOptionsSectionProps {
  facet: Facet;
  showMore: ShowMoreState;
  onShowMoreToggle: (facetName: string) => void;
  selectedCioFacets: SelectedFacets;
  onCheckboxChange: (facetName: string, option: FacetOption) => void;
  dataTestId?: string;
  "data-testid"?: string;
}

/**
 * Generate a URL-safe slug from a string.
 * Replaces slugify dependency with a simple inline implementation.
 */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-");
}

export const FacetOptionsSection = ({
  facet,
  showMore,
  onShowMoreToggle,
  selectedCioFacets,
  onCheckboxChange,
  dataTestId,
}: FacetOptionsSectionProps) => {
  const selectedValues = useMemo(() => {
    return new Set(
      selectedCioFacets[facet.name]?.map(
        (item: SelectedFacetItem) => item.value,
      ) || [],
    );
  }, [selectedCioFacets, facet.name]);

  // Float selected options to the top of the section so an active filter is
  // never buried below the "Show more" cutoff. `Array.prototype.sort` is stable,
  // so the original (merchandised) order is preserved within each group.
  const orderedOptions = useMemo(() => {
    return [...facet.options].sort(
      (a, b) =>
        (selectedValues.has(a.value) ? 0 : 1) -
        (selectedValues.has(b.value) ? 0 : 1),
    );
  }, [facet.options, selectedValues]);

  const handleCheckboxChange = useCallback(
    (facetName: string, option: FacetOption, checked: boolean) => {
      const isCurrentlySelected = selectedValues.has(option.value);
      if (checked !== isCurrentlySelected) {
        onCheckboxChange(facetName, option);
      }
    },
    [selectedValues, onCheckboxChange],
  );

  return (
    <div className="mt-[1px] space-y-[18px]">
      {orderedOptions
        .slice(0, showMore[facet.name] ? orderedOptions.length : 5)
        .map((option: FacetOption) => {
          const isChecked = selectedValues.has(option.value);
          const safeId = slugify(`${facet.name}-${option.value}`);

          return (
            <div key={safeId} className="flex items-center gap-4">
              <Checkbox
                id={safeId}
                checked={isChecked}
                onCheckedChange={checked => {
                  handleCheckboxChange(facet.name, option, !!checked);
                }}
                data-testid={dataTestId || "CioFiltersDrawerContent-Checkbox"}
              />
              <label
                htmlFor={safeId}
                className="cursor-pointer font-grotesque text-[14px] font-light leading-5 text-primary"
              >
                {option.display_name}
                <span className="pl-2 text-xs text-gray-500">
                  ({option.count})
                </span>
              </label>
            </div>
          );
        })}
      {facet.options.length > 5 && (
        <button
          className="mt-5 cursor-pointer font-grotesque text-sm font-light text-foreground underline"
          onClick={() => onShowMoreToggle(facet.name)}
        >
          {showMore[facet.name] ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};
